import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { ALL_OPTION_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useDownloadCSV, useGetEmployerIdParam } from '../../hooks';
import { ClaimsActivityReportTableParams } from '../ClaimsActivityReport.types';

export const QUERY_KEY = 'EXPORT_CLAIMS_ACTIVITY_REPORT_CSV';
const useClaimsActivityReportExportCSVQuery = (params: ClaimsActivityReportTableParams) => {
  const handleSaveFile = useDownloadCSV();
  const getEmployerIdParam = useGetEmployerIdParam({
    isMultipleEmployerMode: params.isMultipleEmployer,
  });
  const [isLoading, setIsLoading] = useState(false);
  const formattedStartDate = useMemo(() => {
    if (!params.startDate) {
      return null;
    }
    const parsedDate = dayjs(params.startDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.startDate]);

  const formattedEndDate = useMemo(() => {
    if (!params.endDate) {
      return null;
    }
    const parsedDate = dayjs(params.endDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.endDate]);

  const submitStartDate = useMemo(() => {
    return params.isClaimSubmitMode ? formattedStartDate : null;
  }, [formattedStartDate, params.isClaimSubmitMode]);

  const submitEndDate = useMemo(() => {
    return params.isClaimSubmitMode ? formattedEndDate : null;
  }, [formattedEndDate, params.isClaimSubmitMode]);

  const paidStartDate = useMemo(() => {
    return params.isClaimSubmitMode ? null : formattedStartDate;
  }, [formattedStartDate, params.isClaimSubmitMode]);
  const padEndDate = useMemo(() => {
    return params.isClaimSubmitMode ? null : formattedEndDate;
  }, [formattedEndDate, params.isClaimSubmitMode]);

  const { refetch } = useQuery(
    [QUERY_KEY],
    () =>
      api.get(API_URL.REPORTS_CLAIM_ACTIVITY_REPORT_BREAKDOWN_EXPORT_CSV, {
        params: _.pickBy({
          ...getEmployerIdParam(params.employerId, params.employerIds),
          show_ssn: params.isSSNShowed,

          isSSNShowed: params.isSSNShowed,

          claim_submit_start_date: submitStartDate,
          claim_submit_end_date: submitEndDate,

          claim_paid_start_date: paidStartDate,
          claim_paid_end_date: padEndDate,

          claim_statuses:
            params?.claimStatuses && !params?.claimStatuses.includes(ALL_OPTION_KEY)
              ? params?.claimStatuses.join(',')
              : undefined,
          claim_types:
            params?.claimTypes && !params?.claimTypes.includes(ALL_OPTION_KEY)
              ? params?.claimTypes.join(',')
              : undefined,
          search_value: params.searchString,
        }),
      }),
    {
      retry: 0,
      enabled: false,
    }
  );

  const handleExport = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await refetch();
      handleSaveFile(response.data?.data, 'csv', 'claims_activity');
    } finally {
      setIsLoading(false);
    }
  }, [handleSaveFile, refetch]);
  return {
    isLoading,
    handleExport,
  };
};

export default useClaimsActivityReportExportCSVQuery;
